import React, { useState, useEffect } from 'react';
import { billingService, BillingPlan } from '../services/billingService';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpgrade: (planCode: string) => void;
  currentPlanCode: string | null;
}

const UpgradeModal: React.FC<UpgradeModalProps> = ({
  isOpen,
  onClose,
  onUpgrade,
  currentPlanCode,
}) => {
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');
  const [selectedPlanCode, setSelectedPlanCode] = useState<string | null>(null);
  const [plans, setPlans] = useState<{ launchpoint: BillingPlan[]; growth_stack: BillingPlan[] } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      setError(null);
      billingService.getPlans()
        .then((data) => {
          setPlans({
            launchpoint: (data.launchpoint?.monthly || []).concat(data.launchpoint?.yearly || []),
            growth_stack: (data.growth_stack?.monthly || []).concat(data.growth_stack?.yearly || []),
          });
        })
        .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load plans'))
        .finally(() => setLoading(false));
    }
  }, [isOpen]);

  const lpPlans = plans
    ? plans.launchpoint.filter((p) => p.billing_interval === billingPeriod)
    : [];
  const gsPlans = plans
    ? plans.growth_stack.filter((p) => p.billing_interval === billingPeriod)
    : [];

  const renderPlanCard = (plan: BillingPlan) => {
    const isSelected = selectedPlanCode === plan.code;
    const isCurrentPlan = currentPlanCode === plan.code;
    const isYearly = plan.billing_interval === 'yearly';

    return (
      <div
        key={plan.code}
        className={`relative bg-surface rounded-2xl shadow-lg border-2 transition-all duration-300 cursor-pointer ${
          isSelected ? 'border-primary shadow-xl scale-105'
            : plan.recommended ? 'border-primary/40 shadow-xl'
            : 'border-border hover:border-border-strong'
        } ${isCurrentPlan ? 'opacity-75' : ''}`}
        onClick={() => !isCurrentPlan && setSelectedPlanCode(plan.code)}
      >
        {plan.recommended && !isCurrentPlan && (
          <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
            <span className="bg-gradient-to-r from-primary to-primary-dark text-primary-fg px-4 py-1 rounded-full text-sm font-medium">
              Most Popular
            </span>
          </div>
        )}
        {isCurrentPlan && (
          <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
            <span className="bg-neutral-badge-bg text-neutral-badge-fg px-4 py-1 rounded-full text-sm font-medium">
              Current Plan
            </span>
          </div>
        )}
        <div className="p-6">
          <div className="text-center mb-6">
            <h3 className="text-xl font-bold text-fg mb-2">{plan.display_name}</h3>
            <p className="text-fg-secondary mb-4 text-sm">{plan.includes_portalr ? 'LaunchPoint + Portalr' : ''}</p>
            <div className="mb-4">
              {isYearly ? (
                <>
                  <span className="text-3xl font-bold text-fg">${plan.monthly_equivalent.toFixed(0)}</span>
                  <span className="text-fg-secondary ml-1">/mo billed annually</span>
                  <div className="text-sm text-fg-muted">${plan.price}/year · Save 2 months</div>
                </>
              ) : (
                <>
                  <span className="text-3xl font-bold text-fg">${plan.price}</span>
                  <span className="text-fg-secondary ml-1">/month</span>
                </>
              )}
            </div>
          </div>
          <ul className="space-y-2 mb-6">
            {(plan.feature_summary || []).slice(0, 6).map((feature, idx) => (
              <li key={idx} className="flex items-start">
                <svg className="w-4 h-4 text-success mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-fg-secondary text-sm">{feature}</span>
              </li>
            ))}
          </ul>
          <button
            className={`w-full py-2 px-4 rounded-xl font-medium transition-all duration-200 ${
              isCurrentPlan ? 'bg-surface-muted text-fg-muted cursor-not-allowed'
                : isSelected ? 'bg-primary text-primary-fg shadow-lg'
                : plan.recommended ? 'bg-gradient-to-r from-primary to-primary-dark text-primary-fg hover:from-primary-dark hover:to-primary'
                : 'border-2 border-border text-fg-secondary hover:border-border-strong'
            }`}
            onClick={(e) => { e.stopPropagation(); if (!isCurrentPlan) setSelectedPlanCode(plan.code); }}
            disabled={isCurrentPlan}
          >
            {isCurrentPlan ? 'Current Plan' : isSelected ? 'Selected' : `Select ${plan.display_name}`}
          </button>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-overlay flex items-center justify-center p-4 z-50">
      <div className="bg-surface rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-surface border-b border-border px-8 py-6 rounded-t-2xl">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-fg">Upgrade Your Plan</h2>
              <p className="text-fg-secondary mt-1">Choose a plan and billing frequency.</p>
            </div>
            <button onClick={onClose} className="text-fg-muted hover:text-fg-secondary transition-colors">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        </div>

        <div className="px-8 py-6">
          <div className="flex justify-center mb-8">
            <div className="bg-surface-muted rounded-xl p-1 flex">
              <button
                onClick={() => setBillingPeriod('monthly')}
                className={`px-6 py-2 rounded-lg font-medium transition-all duration-200 ${
                  billingPeriod === 'monthly' ? 'bg-surface text-fg shadow-sm' : 'text-fg-secondary hover:text-fg'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingPeriod('yearly')}
                className={`px-6 py-2 rounded-lg font-medium transition-all duration-200 relative ${
                  billingPeriod === 'yearly' ? 'bg-surface text-fg shadow-sm' : 'text-fg-secondary hover:text-fg'
                }`}
              >
                Yearly
                <span className="absolute -top-2 -right-2 bg-success text-primary-fg text-xs px-2 py-1 rounded-full">Save 2 months</span>
              </button>
            </div>
          </div>

          {loading && (
            <div className="text-center py-12"><p className="text-fg-secondary">Loading plans...</p></div>
          )}
          {error && (
            <div className="text-center py-4"><p className="text-danger">{error}</p></div>
          )}
          {!loading && !error && plans && (
            <>
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-fg mb-4">LaunchPoint Plans</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {lpPlans.map(renderPlanCard)}
                </div>
              </div>
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-fg mb-4">Growth Stack (Includes Portalr)</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {gsPlans.map(renderPlanCard)}
                </div>
              </div>

              <div className="flex justify-between items-center mt-8 pt-6 border-t border-border">
                <button onClick={onClose} className="px-6 py-2 text-fg-secondary hover:text-fg font-medium transition-colors duration-200">
                  Maybe Later
                </button>
                <button
                  onClick={() => selectedPlanCode && onUpgrade(selectedPlanCode)}
                  disabled={!selectedPlanCode}
                  className="bg-gradient-to-r from-primary to-primary-dark hover:from-primary-dark hover:to-primary text-primary-fg px-8 py-3 rounded-lg font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {selectedPlanCode ? `Upgrade` : 'Select a Plan'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UpgradeModal;
